import React from 'react'
import isNil from 'lodash/isNil'
import { message, Tooltip } from 'antd'
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import {
  AdjustmentLineDto,
  HealthPlanLineDto,
  MedicareLineDto,
  PlanCostDto,
  YtdLineDto,
} from '../../../api/dto/generated-invoice.dto'
import {
  BenefitIdButton,
  PlanCostBer,
  PlanCostCell,
  PlanCostInvoice,
} from './InvoiceGenerationView.styles'
import {
  billingMonthLabelSortKey,
  formatBillingMonth,
  formatCurrency,
  truncateId,
} from './utils'

const copyToClipboard = (value: string): void => {
  if (!navigator.clipboard?.writeText) {
    message.error('Failed to copy Benefit ID')
    return
  }
  navigator.clipboard
    .writeText(value)
    .then(() => message.success('Benefit ID copied'))
    .catch(() => message.error('Failed to copy Benefit ID'))
}

// Opts the stacked Plan Cost cell into flex centring (see DrawerGridPane).
const PLAN_COST_CELL_CLASS = 'plan-cost-cell'

const PlanCostCellRenderer = ({
  data,
}: ICellRendererParams<{ planCost: PlanCostDto }>): React.ReactNode => {
  if (isNil(data)) return null
  const { planCost } = data
  return (
    <PlanCostCell>
      <PlanCostInvoice $credit={planCost.invoice < 0}>
        {formatCurrency(planCost.invoice)}
      </PlanCostInvoice>
      {!isNil(planCost.ber) && (
        <PlanCostBer>BER: {formatCurrency(planCost.ber)}</PlanCostBer>
      )}
    </PlanCostCell>
  )
}

const BenefitIdCellRenderer = ({
  value,
}: ICellRendererParams<unknown, string | null>): React.ReactNode => {
  if (!value) return '—'
  return (
    <Tooltip title={`${value} (click to copy)`}>
      <BenefitIdButton
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          copyToClipboard(value)
        }}
      >
        {truncateId(value)}
      </BenefitIdButton>
    </Tooltip>
  )
}

// Empty cells read as an em dash rather than a blank; the set filter still
// groups them under its own "(Blanks)" entry.
const dashFormatter = ({ value }: ValueFormatterParams): string =>
  isNil(value) || value === '' ? '—' : String(value)

const currencyFormatter = ({ value }: ValueFormatterParams): string =>
  typeof value === 'number' ? formatCurrency(value) : '—'

/**
 * Money columns: right-aligned and sortable by their numeric value, but not
 * filterable — picking an exact dollar amount off a checkbox list is noise.
 */
const moneyColumn = <TRow,>(colDef: ColDef<TRow>): ColDef<TRow> => ({
  type: 'numericColumn',
  filter: false,
  valueFormatter: currencyFormatter,
  ...colDef,
})

/** Chronological ordering for both the column sort and the filter value list. */
const monthComparator = (left?: string | null, right?: string | null): number =>
  billingMonthLabelSortKey(left) - billingMonthLabelSortKey(right)

const MONTH_COLUMN: Pick<ColDef, 'comparator' | 'filterParams'> = {
  comparator: monthComparator,
  filterParams: { comparator: monthComparator },
}

// High-cardinality free text (IDs, notes) reads better as a "contains" search
// than as a checkbox list of near-unique values.
const TEXT_FILTER: Pick<ColDef, 'filter'> = { filter: 'agTextColumnFilter' }

const LINE_TYPE_LABELS: Record<string, string> = {
  CHARGE: 'Charge',
  CREDIT: 'Credit',
}

const lineTypeFormatter = ({ value }: ValueFormatterParams): string =>
  (typeof value === 'string' && LINE_TYPE_LABELS[value]) || '—'

/**
 * Row shape shared by the Health Plans and Adjustments grids: a Health Plans
 * line, carrying `type` when it comes from the Adjustments tab. ag-grid's
 * ColDef is invariant in its row type, so both grids are typed on this one
 * shape and the drawer passes it as the grid's type argument.
 */
export type InvoiceLineRow = HealthPlanLineDto &
  Partial<Pick<AdjustmentLineDto, 'type'>>

// Column groups shared by the Health Plans and Adjustments grids.
const monthColumn: ColDef<InvoiceLineRow> = {
  headerName: 'Month',
  colId: 'month',
  width: 110,
  valueGetter: ({ data }) =>
    isNil(data)
      ? null
      : formatBillingMonth(data.billingMonth, data.billingYear),
  ...MONTH_COLUMN,
}

const memberColumns: ColDef<InvoiceLineRow>[] = [
  {
    headerName: 'Employee ID',
    field: 'employeeId',
    width: 120,
    valueFormatter: dashFormatter,
    ...TEXT_FILTER,
  },
  {
    headerName: 'Company Code',
    field: 'companyCode',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Division Code',
    field: 'divisionCode',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Department',
    field: 'department',
    width: 150,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Benefit ID',
    field: 'benefitId',
    width: 130,
    cellRenderer: BenefitIdCellRenderer,
    ...TEXT_FILTER,
  },
  {
    headerName: 'Employee',
    field: 'employeeName',
    width: 180,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Relationship',
    field: 'relationship',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Carrier',
    field: 'carrierName',
    width: 210,
    valueFormatter: dashFormatter,
  },
]

const notesColumn: ColDef<InvoiceLineRow> = {
  headerName: 'Notes',
  field: 'notes',
  width: 300,
  valueFormatter: dashFormatter,
  tooltipField: 'notes',
  ...TEXT_FILTER,
}

const benefitWindowColumns: ColDef<InvoiceLineRow>[] = [
  {
    headerName: 'Benefit Effective Date',
    field: 'benefitEffectiveDate',
    width: 170,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Benefit End Date',
    field: 'benefitEndDate',
    width: 160,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Employment Status',
    field: 'employmentStatus',
    width: 170,
    valueFormatter: dashFormatter,
  },
]

// Column definitions for the Health Plans, Adjustments, Medicare, and YTD grids.
// Declarative config extracted from the drawer so the drawer stays
// orchestration-only.
//
// Health Plans holds the funded month's premiums only, so it carries no Credit
// Reason column; credits and corrections live on the Adjustments grid.
export const healthPlanColumns: ColDef<InvoiceLineRow>[] = [
  monthColumn,
  ...memberColumns,
  moneyColumn<InvoiceLineRow>({
    headerName: 'Plan Cost',
    colId: 'planCost',
    width: 130,
    valueGetter: ({ data }) => data?.planCost.invoice ?? null,
    cellRenderer: PlanCostCellRenderer,
    cellClass: PLAN_COST_CELL_CLASS,
  }),
  moneyColumn<InvoiceLineRow>({
    headerName: 'Employer Monthly Contribution',
    field: 'employer',
    width: 220,
  }),
  moneyColumn<InvoiceLineRow>({
    headerName: 'Employee Monthly Cost',
    field: 'employee',
    width: 190,
  }),
  moneyColumn<InvoiceLineRow>({
    headerName: 'Employee Payroll Deduction (pre-tax)',
    field: 'employeePayrollDeductionPreTax',
    width: 240,
  }),
  moneyColumn<InvoiceLineRow>({
    headerName: 'Employee Payroll Deduction (post-tax for Domestic Partners)',
    field: 'employeePayrollDeductionPostTax',
    width: 340,
  }),
  {
    headerName: 'Payroll Cycle',
    field: 'payrollCycle',
    width: 130,
    valueFormatter: dashFormatter,
  },
  notesColumn,
  ...benefitWindowColumns,
]

// Adjustments: credits, other-month charges and funded-month corrections. The
// signed amount keeps the stacked invoice-over-BER cell so a correction can be
// read against the premium it adjusts; Credit Reason and Notes explain it.
export const adjustmentColumns: ColDef<InvoiceLineRow>[] = [
  monthColumn,
  {
    headerName: 'Type',
    field: 'type',
    width: 100,
    valueFormatter: lineTypeFormatter,
    filterParams: { valueFormatter: lineTypeFormatter },
  },
  ...memberColumns,
  moneyColumn<InvoiceLineRow>({
    headerName: 'Amount',
    colId: 'amount',
    width: 130,
    valueGetter: ({ data }) => data?.planCost.invoice ?? null,
    cellRenderer: PlanCostCellRenderer,
    cellClass: PLAN_COST_CELL_CLASS,
  }),
  {
    headerName: 'Credit Reason',
    field: 'creditReason',
    width: 180,
    valueFormatter: dashFormatter,
  },
  notesColumn,
  ...benefitWindowColumns,
]

export const medicareColumns: ColDef<MedicareLineDto>[] = [
  {
    headerName: 'Start Date',
    field: 'benefitEffectiveDate',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'End Date',
    field: 'benefitEndDate',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Employee ID',
    field: 'employeeId',
    width: 120,
    valueFormatter: dashFormatter,
    ...TEXT_FILTER,
  },
  {
    headerName: 'Company Code',
    field: 'companyCode',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Division Code',
    field: 'divisionCode',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Department',
    field: 'department',
    width: 150,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Department Code',
    field: 'departmentCode',
    width: 150,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'First Name',
    field: 'firstName',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Last Name',
    field: 'lastName',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Relationship',
    field: 'relationship',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Carrier',
    field: 'carrierName',
    width: 210,
    valueFormatter: dashFormatter,
  },
  moneyColumn<MedicareLineDto>({
    headerName: 'Employer Monthly Contribution',
    field: 'employerMonthlyContribution',
    width: 220,
  }),
  moneyColumn<MedicareLineDto>({
    headerName: 'Approved Monthly Reimbursement',
    field: 'approvedMonthlyReimbursement',
    width: 230,
  }),
  moneyColumn<MedicareLineDto>({
    headerName: 'Employee Payroll Pre-Tax Reimbursement',
    field: 'employeePayrollPreTaxReimbursement',
    width: 260,
  }),
  moneyColumn<MedicareLineDto>({
    headerName: 'Employee Payroll Post-Tax Reimbursement',
    field: 'employeePayrollPostTaxReimbursement',
    width: 270,
  }),
  {
    headerName: 'Payroll Cycle',
    field: 'payrollCycle',
    width: 130,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Notes',
    field: 'notes',
    width: 300,
    valueFormatter: dashFormatter,
    tooltipField: 'notes',
    ...TEXT_FILTER,
  },
  {
    headerName: 'Benefit ID',
    field: 'benefitId',
    width: 130,
    cellRenderer: BenefitIdCellRenderer,
    ...TEXT_FILTER,
  },
]

export const ytdColumns: ColDef<YtdLineDto>[] = [
  {
    headerName: 'Month',
    field: 'month',
    width: 130,
    valueFormatter: dashFormatter,
    ...MONTH_COLUMN,
  },
  {
    headerName: 'Benefit ID',
    field: 'benefitId',
    width: 130,
    cellRenderer: BenefitIdCellRenderer,
    ...TEXT_FILTER,
  },
  {
    headerName: 'Employee',
    field: 'employeeName',
    width: 180,
    valueFormatter: dashFormatter,
  },
  {
    headerName: 'Carrier',
    field: 'carrierName',
    width: 210,
    valueFormatter: dashFormatter,
  },
  moneyColumn<YtdLineDto>({
    headerName: 'Plan Cost',
    colId: 'planCost',
    width: 130,
    valueGetter: ({ data }) => data?.planCost.invoice ?? null,
    cellRenderer: PlanCostCellRenderer,
    cellClass: PLAN_COST_CELL_CLASS,
  }),
  moneyColumn<YtdLineDto>({
    headerName: 'Employer',
    field: 'employer',
    width: 150,
  }),
  moneyColumn<YtdLineDto>({
    headerName: 'Employee Cost',
    field: 'employee',
    width: 150,
  }),
  {
    headerName: 'Credit Reason',
    field: 'creditReason',
    width: 180,
    valueFormatter: dashFormatter,
  },
]
